import type { TensionBucket } from '@tunasong/music-lib'
import { spokenText } from '../theory-copy/spoken-text.js'

/**
 * Tension Contour Stripe — pure presentation helpers (Joni visual spec §3, §9).
 *
 * The under-stripe is **dual-encoded** from ONE bucket lookup so its two
 * channels can never disagree (WCAG 2.2 SC 1.4.1 — hue is never the sole
 * carrier):
 *   - hue   — a cool→warm `--tuna-tension-*` token, applied via the CSS module
 *             class returned by {@link tensionStripeClass}.
 *   - height — a redundant non-colour channel (1px rest → 5px peak), driven by
 *             {@link getTensionStripeHeightPx} as an inline style so TS owns a
 *             single source of truth (no CSS/TS px drift).
 *
 * Both functions take the SAME `bucket`, so the channels are structurally
 * locked together.
 *
 * Model: docs/superpowers/specs/2026-06-03-tension-contour-stripe-model-spec.md
 * Visual: docs/superpowers/specs/2026-06-03-tension-contour-stripe-visual-spec.md
 */

/** Logical class token per bucket — resolved against the CSS module by the chip. */
const BUCKET_CLASS: Record<TensionBucket, string> = {
  rest: 'tensionStripe--rest',
  low: 'tensionStripe--low',
  medium: 'tensionStripe--medium',
  high: 'tensionStripe--high',
  peak: 'tensionStripe--peak',
}

/** Modifier token for the muted (low-confidence) treatment — desaturated + dashed. */
const MUTED_CLASS = 'tensionStripe--muted'

/** Height in px per bucket — the redundant non-colour channel (visual spec §3). */
const BUCKET_HEIGHT_PX: Record<TensionBucket, number> = {
  rest: 1,
  low: 2,
  medium: 3,
  high: 4,
  peak: 5,
}

/** Fixed inline a11y hedge for low-confidence stripes (Bowie: NOT a lexicon entry). */
const UNCERTAIN_ARIA_PHRASE = 'harmonic tension: uncertain'

/**
 * Logical class tokens for a stripe — the bucket hue class, plus the muted
 * modifier when low-confidence. Space-joined; the muted string CONTAINS the
 * confident string so the bucket hue still resolves underneath the muting.
 */
export const tensionStripeClass = (bucket: TensionBucket, lowConfidence: boolean): string =>
  lowConfidence ? `${BUCKET_CLASS[bucket]} ${MUTED_CLASS}` : BUCKET_CLASS[bucket]

/** Stripe height in px for a bucket — the SC 1.4.1 redundant channel. */
export const getTensionStripeHeightPx = (bucket: TensionBucket): number => BUCKET_HEIGHT_PX[bucket]

/**
 * Spoken a11y phrase for the chip's aria-label.
 *
 * Confident: `"harmonic tension: <tagLabel>"`, glyphs spoken via `spokenText`.
 * Low-confidence: the fixed inline `"harmonic tension: uncertain"` hedge —
 * Bowie's call that this is UI-status microcopy, not lexicon copy.
 */
export const buildTensionAriaPhrase = (tagLabel: string, lowConfidence: boolean): string =>
  lowConfidence ? UNCERTAIN_ARIA_PHRASE : spokenText(`harmonic tension: ${tagLabel}`)
